import { ReactNode, useEffect } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import styled from "@emotion/styled";
import { Global, css } from "@emotion/react";

// Leaflet 기본 마커 아이콘 수정 (CSS 문제 해결)
if (typeof window !== "undefined" && L && L.Icon && L.Icon.Default) {
  try {
    // _getIconUrl이 존재하는 경우에만 삭제
    if ((L.Icon.Default.prototype as any)._getIconUrl) {
      delete (L.Icon.Default.prototype as any)._getIconUrl;
    }
    
    // mergeOptions 전에 Default 아이콘이 제대로 초기화되었는지 확인
    if (L.Icon.Default.prototype && typeof L.Icon.Default.prototype.createIcon === 'function') {
      L.Icon.Default.mergeOptions({
        iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
        iconUrl: require("leaflet/dist/images/marker-icon.png"),
        shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
      });
    } else {
      // createIcon이 없으면 직접 설정
      (L.Icon.Default.prototype as any).iconUrl = require("leaflet/dist/images/marker-icon.png");
      (L.Icon.Default.prototype as any).iconRetinaUrl = require("leaflet/dist/images/marker-icon-2x.png");
      (L.Icon.Default.prototype as any).shadowUrl = require("leaflet/dist/images/marker-shadow.png");
    }
  } catch (error) {
    console.error("Failed to initialize Leaflet default icon:", error);
  }
}

// 지도 이벤트 핸들러 컴포넌트
interface MapEventsProps {
  onIdle?: (map: L.Map) => void;
  onClick?: () => void;
}

const MapEvents: React.FC<MapEventsProps> = ({ onIdle, onClick }) => {
  const map = useMap();

  useEffect(() => {
    if (onIdle) {
      const handleMoveEnd = () => {
        onIdle(map);
      };

      map.on("moveend", handleMoveEnd);
      map.on("zoomend", handleMoveEnd);

      return () => {
        map.off("moveend", handleMoveEnd);
        map.off("zoomend", handleMoveEnd);
      };
    }
  }, [map, onIdle]);

  useEffect(() => {
    if (onClick) {
      map.on("click", onClick);
      return () => {
        map.off("click", onClick);
      };
    }
  }, [map, onClick]);

  return null;
};

// 지도 중심 및 줌 레벨 업데이트 컴포넌트
interface MapUpdaterProps {
  center: { lat: number; lng: number };
  zoom: number;
  onCreate?: (map: L.Map) => void;
}

const MapUpdater: React.FC<MapUpdaterProps> = ({ center, zoom, onCreate }) => {
  const map = useMap();

  useEffect(() => {
    // animate: false로 설정하여 애니메이션 없이 즉시 이동
    map.setView([center.lat, center.lng], zoom, { animate: false });
  }, [map, center.lat, center.lng, zoom]);

  // onCreate 콜백 실행 (최초 1회)
  useEffect(() => {
    if (onCreate) {
      onCreate(map);
    }
  }, [map]); // map만 의존성에 넣어서 최초 1회만 실행

  return null;
};

// 지도 컨테이너 Props
export interface LeafletMapWrapperProps {
  center: { lat: number; lng: number };
  zoom: number;
  style?: React.CSSProperties;
  children?: ReactNode;
  onIdle?: (map: L.Map) => void;
  onClick?: () => void;
  onCreate?: (map: L.Map) => void;
  mapTypeId?: number; // 1: 일반, 3: 위성 (카카오맵 호환)
  zoomControl?: boolean; // 확대/축소 버튼 표시 여부
}

/**
 * Leaflet 지도 래퍼 컴포넌트
 * 카카오맵과 동일한 인터페이스 제공
 */
export const LeafletMapWrapper: React.FC<LeafletMapWrapperProps> = ({
  center,
  zoom,
  style,
  children,
  onIdle,
  onClick,
  onCreate,
  mapTypeId = 1,
  zoomControl = false, // 기본값: 확대/축소 버튼 숨김
}) => {
  // 지도 타입에 따른 타일 레이어 선택
  const getTileLayer = () => {
    if (mapTypeId === 3) {
      // 위성 지도 (Esri World Imagery)
      return {
        url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
        attribution:
          "Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community",
      };
    } else {
      // 일반 지도 (OpenStreetMap)
      return {
        url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      };
    }
  };

  const tileLayer = getTileLayer();

  return (
    <>
      <Global
        styles={css`
          /* 커스텀 DivIcon 스타일 - Leaflet 기본 스타일 제거 */
          .custom-div-icon {
            background: none !important;
            border: none !important;
          }
          
          .invisible-marker {
            background: none !important;
            border: none !important;
            width: 1px !important;
            height: 1px !important;
          }
          
          .leaflet-popup-content-wrapper {
            padding: 0;
            border-radius: 0;
            background: transparent;
            box-shadow: none;
          }
          
          .leaflet-popup-content {
            margin: 0;
            width: auto !important;
          }
          
          .leaflet-popup-content p {
            margin: 0 !important;
          }
          
          .leaflet-popup-tip {
            display: none;
          }
        `}
      />
      <MapContainer
        center={[center.lat, center.lng]}
        zoom={zoom}
        style={{ width: "100%", height: "100%", zIndex: 0, ...style }}
        zoomControl={zoomControl}
        zoomAnimation={false}
        fadeAnimation={false}
        markerZoomAnimation={false}
        preferCanvas={true}
        zoomSnap={1}
        zoomDelta={1}
      >
        <TileLayer 
          key={mapTypeId} 
          url={tileLayer.url} 
          attribution={tileLayer.attribution} 
        />
        {/* 위성 지도일 때 Esri Reference 레이어 오버레이 (전문 오버레이 레이어) */}
        {mapTypeId === 3 && (
          <>
            {/* 도로/교통 레이어 */}
            <TileLayer
              key={`${mapTypeId}-transportation`}
              url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}"
              attribution="&copy; Esri"
              opacity={0.8}
            />
            {/* 지명/라벨 레이어 */}
            <TileLayer
              key={`${mapTypeId}-labels`}
              url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Reference_Overlay/MapServer/tile/{z}/{y}/{x}"
              attribution="&copy; Esri"
              opacity={0.7}
            />
            {/* 경계선 레이어 */}
            <TileLayer
              key={`${mapTypeId}-boundaries`}
              url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
              attribution="&copy; Esri"
              opacity={0.6}
            />
          </>
        )}
        <MapEvents onIdle={onIdle} onClick={onClick} />
        <MapUpdater center={center} zoom={zoom} onCreate={onCreate} />
        {children}
      </MapContainer>
    </>
  );
};

// Leaflet 마커 Props
export interface LeafletMarkerProps {
  position: { lat: number; lng: number };
  icon?: string; // 이미지 URL
  iconSize?: { width: number; height: number };
  iconAnchor?: { x: number; y: number }; // 카카오맵 offset과 동일한 형식
  onClick?: () => void;
  children?: ReactNode;
  zIndex?: number;
}

/**
 * Leaflet 마커 컴포넌트
 * 카카오맵 MapMarker와 호환되는 인터페이스
 */
export const LeafletMarker: React.FC<LeafletMarkerProps> = ({
  position,
  icon,
  iconSize = { width: 40, height: 44 },
  iconAnchor,
  onClick,
  children,
  zIndex,
}) => {
  // 커스텀 아이콘 생성
  let customIcon: L.Icon | L.DivIcon | undefined;
  
  if (icon && L && L.Icon && L.icon) {
    try {
      // L.Icon이 제대로 초기화되었는지 확인
      if (L.Icon.prototype && typeof L.Icon.prototype.createIcon === 'function') {
        customIcon = L.icon({
          iconUrl: icon,
          iconSize: [iconSize.width, iconSize.height],
          // iconAnchor가 제공되면 사용, 아니면 기본값 (하단 중앙)
          iconAnchor: iconAnchor 
            ? [iconAnchor.x, iconAnchor.y] 
            : [iconSize.width / 2, iconSize.height],
          popupAnchor: [0, -iconSize.height],
        });
      } else {
        console.warn("L.Icon.prototype.createIcon is not available, using default icon");
        // 기본 아이콘 명시적으로 생성
        if (L.Icon && L.Icon.Default && L.Icon.Default.prototype) {
          try {
            customIcon = new L.Icon.Default();
          } catch (defaultError) {
            console.error("Failed to create L.Icon.Default:", defaultError);
            customIcon = undefined;
          }
        }
      }
    } catch (error) {
      console.error("Failed to create Leaflet icon:", error);
      // 아이콘 생성 실패 시 기본 아이콘 사용
      if (L.Icon && L.Icon.Default) {
        try {
          customIcon = new L.Icon.Default();
        } catch (defaultError) {
          console.error("Failed to create default icon:", defaultError);
          customIcon = undefined;
        }
      }
    }
  } else if (!icon && L && L.Icon && L.Icon.Default) {
    // icon이 없을 때 기본 아이콘 명시적으로 생성
    try {
      customIcon = new L.Icon.Default();
    } catch (error) {
      console.error("Failed to create default icon:", error);
      customIcon = undefined;
    }
  }

  // onClick이 있을 때만 eventHandlers 추가
  const eventHandlers = onClick ? { click: onClick } : undefined;

  return (
    <Marker
      position={[position.lat, position.lng]}
      icon={customIcon}
      eventHandlers={eventHandlers}
      zIndexOffset={zIndex}
    >
      {children && <Popup>{children}</Popup>}
    </Marker>
  );
};

// Leaflet 폴리라인 Props
export interface LeafletPolylineProps {
  path: { lat: number; lng: number }[];
  strokeWeight?: number;
  strokeColor?: string;
  strokeOpacity?: number;
}

/**
 * Leaflet 폴리라인 컴포넌트
 */

export const LeafletPolyline: React.FC<LeafletPolylineProps> = ({
  path,
  strokeWeight = 5,
  strokeColor = "#3388ff",
  strokeOpacity = 0.7,
}) => {
  const positions: [number, number][] = path.map((p) => [p.lat, p.lng]);

  return (
    <Polyline
      positions={positions}
      pathOptions={{
        color: strokeColor,
        weight: strokeWeight,
        opacity: strokeOpacity,
      }}
    />
  );
};

// CSS 오버레이용 래퍼
export const LeafletOverlay = styled.div`
  /* 커스텀 오버레이 스타일 */
  position: relative;
  z-index: 1000;
`;

